// packages/frontend/src/components/shared/EmptyState.tsx
// Unified EmptyState component supporting multiple usage patterns

import type { ReactNode } from 'react'
import { DocumentTextIcon } from '@heroicons/react/24/outline'

/**
 * Action button configuration object
 */
export interface EmptyStateAction {
  label: string
  onClick: () => void
  testId?: string
}

/**
 * EmptyState component props
 * Supports two patterns:
 * 1. title/description pattern (legacy): <EmptyState title="..." description="..." />
 * 2. message/description pattern: <EmptyState message="..." description="..." />
 *
 * Action can be either:
 * 1. Object with onClick/label: action={{ onClick: fn, label: "Click" }}
 * 2. ReactNode: action={<Button>Click</Button>}
 */
export interface EmptyStateProps {
  /** Primary text - use either title or message (both work identically) */
  title?: string
  /** Primary text - alias for title (for backwards compatibility) */
  message?: string
  /** Secondary text */
  description?: string
  /** Icon to display */
  icon?: ReactNode
  /** Action button - either object with onClick/label or ReactNode */
  action?: EmptyStateAction | ReactNode
  /** Test ID for the container */
  testId?: string
  /** Optional className for additional styling */
  className?: string
}

/**
 * Type guard to check if action is an EmptyStateAction object
 */
function isActionObject(action: EmptyStateAction | ReactNode): action is EmptyStateAction {
  return (
    action !== null &&
    typeof action === 'object' &&
    'onClick' in action &&
    'label' in action &&
    typeof (action as EmptyStateAction).onClick === 'function'
  )
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  message,
  description,
  icon,
  action,
  testId,
  className = ''
}) => {
  // Use title or message (both are valid)
  const displayTitle = title || message || 'No data'

  return (
    <div
      className={`empty-state ${className}`}
      data-testid={testId || 'empty-state'}
    >
      {/* Icon */}
      <div className="empty-state-icon">
        {icon || <DocumentTextIcon style={{ width: 64, height: 64 }} />}
      </div>

      {/* Title */}
      <h2 className="empty-state-title">{displayTitle}</h2>

      {/* Description */}
      {description && (
        <p className="empty-state-description">{description}</p>
      )}

      {/* Action */}
      {action && (
        <div className="empty-state-action">
          {isActionObject(action) ? (
            <button
              className="empty-state-cta"
              onClick={action.onClick}
              data-testid={action.testId}
            >
              {action.label}
            </button>
          ) : (
            action
          )}
        </div>
      )}
    </div>
  )
}

// Also export as default for flexibility
export default EmptyState
